
import Destination from '@/components/Passenger/models/Destination';
import { Organization } from '@/models/Organization';
import { RideitInvitation, RideitInvitationMessageDirective } from '@/models/Rideit/RideitInvitation';
import RideitPassengerToken from '@/models/Rideit/RideitPassengerToken';
import restService from '@/services/restService';
import { IDataTablePagination } from '@/typings/dataTable';
import { IPaginatedPassengerRequest } from '@/typings/timit';
import {Component, Prop, Vue} from 'vue-property-decorator';
import RideitInvitationFilterForm, { RideitInvitationFilterFormData } from './RideitInvitationFilterForm.vue';
import RideitInvitationTable from './RideitInvitationTable.vue';
import { getErrorMessage } from '@/util/errorMessageHelper';
import RideitInvitationForm from './RideitInvitationForm.vue';
import Group from '@/components/Passenger/models/Group';
import PassengerWithSms from '@/components/Passenger/models/PassengerWithSms';
import vuetifyToast from 'vuetify-toast';
import RideitInvitationFormData from '@/models/Rideit/RideitInvitationFormData';

@Component({
  components: {
      RideitInvitationFilterForm,
      RideitInvitationTable,
      RideitInvitationForm,
  }
})
export default class RideitInvitations extends Vue {
  loading = false;

  pagination: IDataTablePagination = {
    rowsPerPage: 20, // -1 for All,
    page: 1,
    sortBy: 'name',
    descending: false,
    totalItems: 0,
  }

  filters: RideitInvitationFilterFormData = {
    passengerSearch: '',
    destinationId: null,
    tag: '',
  };

  invitationForm: RideitInvitationFormData = new RideitInvitationFormData({
    message: this.$t('rideIt.invitations_default_message', { link: RideitInvitationMessageDirective }).toString(),
    onlyPassengersWithoutActiveTokens: false,
    onlyNewInvitations: true,
    selected: [],
  });

  organization: Organization = this.getOrganization();
  invitations: RideitInvitation[] = [];
  tokens: RideitPassengerToken[] = [];
  passengers: PassengerWithSms[] = [];
  groups: Group[] = [];

  errorMessage: string = '';


  get destinations() {
    const destinations: Destination[] = this.$store.state.destination.destinations;
    return destinations.filter(d => d.organizationId === this.organization.id);
  }

  getOrganization() {
    const organizationId = this.$route.params.organizationId;
    return this.$store.state.organization.organizations.find((o: Organization) => o.id === organizationId);
  }

  shouldUpdatePassengers(newFilters: RideitInvitationFilterFormData | null, newPagination: IDataTablePagination | null) {
    if (!newFilters && !newPagination) {
      // Nothing has changed
      return false;
    }
    if (newFilters && !this.filtersChanged(newFilters)) {
      // Filters haven't changed in a meaningful way
      return false;
    }

    if (newPagination && !this.paginationChanged(newPagination)) {
      // Pagination hasn't changed in a meaningful way
      return false;
    }

    if (!newFilters && this.filtersAreEmpty()) {
      // Filters are empty, pagination changes shouldn't trigger update
      return false;
    }

    if (newPagination && this.onlyNonSupportedSortByChanged(newPagination)) {
      // Only non-supported sortBy values have changed, update shouldn't be triggered
      return false;
    }

    return true;
  }
  
  filtersChanged(newFilters: RideitInvitationFilterFormData) {
    const valueChanges = [
      newFilters.passengerSearch !== this.filters.passengerSearch && 
      !this.isNotLongEnoughSearch(newFilters.passengerSearch),
      newFilters.destinationId !== this.filters.destinationId,
      newFilters.tag !== this.filters.tag,
    ];
    return valueChanges.some(change => change);
  }

  paginationChanged(newPagination: IDataTablePagination) {
    const valueChanges = [
      newPagination.page !== this.pagination.page,
      newPagination.rowsPerPage !== this.pagination.rowsPerPage,
      newPagination.sortBy !== this.pagination.sortBy,
      newPagination.descending !== this.pagination.descending,
    ];
    return valueChanges.some(change => change);
  }

  isNotLongEnoughSearch(searchTerm: string) {
    return searchTerm.length > 0 && searchTerm.length < 3;
  }

  filtersAreEmpty() {
    return !this.filters.passengerSearch && !this.filters.destinationId && !this.filters.tag;
  }

  onlyNonSupportedSortByChanged(newPagination: IDataTablePagination) { 
    const nonSortbyPropertyChanges = [
      newPagination.page !== this.pagination.page,
      newPagination.rowsPerPage !== this.pagination.rowsPerPage,
    ];

    if (nonSortbyPropertyChanges.some(change => change)) {
      return false;
    }

    const nonSupportedSortByValues = ['invitations', 'tokens', 'newTokensAmount'];
    return newPagination.sortBy && nonSupportedSortByValues.includes(newPagination.sortBy);
  }

  async onFiltersChange(newFilters: RideitInvitationFilterFormData) {
    const shouldUpdate = this.shouldUpdatePassengers(newFilters, null);
    this.filters = newFilters;


    if (!shouldUpdate) {
      return;
    }

    await this.updatePassengers();
  }

  async onPaginationChange(newPagination: IDataTablePagination) {
    const shouldUpdate = this.shouldUpdatePassengers(null, newPagination);
    this.pagination = newPagination;

    if (!shouldUpdate) {
      return;
    }

    await this.updatePassengers();
  }

  async updatePassengers() {
    if (this.filtersAreEmpty()) {
      // If filters are empty, we don't want to show any passengers, as that could be a very long list. 
      // Instead we require the user to input some filter criteria first.
      this.passengers = [];
      this.invitationForm.selected = [];
      this.pagination.totalItems = 0;
      this.$store.dispatch('passenger/upsertPassengers', this.passengers)
      return;
    }

    try {
      this.loading = true;
      this.invitationForm.selected = [];
      const result = await this.getPaginatedPassengers(this.filters, this.pagination);
      this.pagination.totalItems = result.itemCount;
      this.pagination.rowsPerPage = result.pageSize;
      this.pagination.page = result.page;
      this.passengers = result.items.map(p => new PassengerWithSms(p));
      this.$store.dispatch('passenger/upsertPassengers', this.passengers);
    } catch (error) {
      this.errorMessage = getErrorMessage(error);
    } finally {
      this.loading = false;
    }
  }

  async getTokens(organizationId: string) {
    const tokens = await restService.rideit.getPassengerTokens(organizationId, true);
    return tokens.map(t => new RideitPassengerToken(t));
  }

  async getInvitations(organizationId: string) {
    const invitations = await restService.rideit.getInvitations(organizationId);
    return invitations.map(i => new RideitInvitation(i));
  }

  async getPaginatedPassengers(filters: RideitInvitationFilterFormData, pagination: IDataTablePagination) {
    const destinationIds = filters.destinationId ? [filters.destinationId] : this.destinations.map(d => d.id);

    const request: IPaginatedPassengerRequest = {
      destinationIds,
      index: pagination.page ? pagination.page - 1 : 0,
      count: pagination.rowsPerPage,
      sortBy: pagination.sortBy ?? null,
      descending: pagination.descending ?? false,
      nameOrYearGradeSearch: filters.passengerSearch,
      onlyActive: true,
      excludeIds: [],
      groups: filters.tag,
    };

    const result = await restService.passengers.getPaginatedPassengersWithSms(request);
    return result;
  }

  async sendInvitations() {
    const invitationsRequest = this.invitationForm.createInvitationsRequest;

    try {
      this.loading = true;
      const result =await restService.rideit.createInvitations(invitationsRequest, this.organization.id);
      if (!result) {
        return vuetifyToast.error(this.$t('rideIt.invitations_failed', { errorMessage: 'Unknown error' }));
      }
      vuetifyToast.success(this.$t('rideIt.invitations_success'));
    } catch (error) {
      this.errorMessage = getErrorMessage(error);
      vuetifyToast.error(this.$t('rideIt.invitations_failed', { errorMessage: this.errorMessage }));
    } finally {
      this.loading = false;
    }
  }

  async getGroups(organizationId: string) {
    const groups = await restService.organizations.getPassengersGroups(organizationId);
    return groups.map(g => new Group(g));
  }

  async cancelToken(token: RideitPassengerToken) {
    try {
      this.loading = true;
      const deletedToken = await restService.rideit.cancelToken(token.id, this.organization.id);
      // Remove the cancelled token from the list
      this.tokens = this.tokens.filter(t => t.id !== deletedToken.id);
    } catch (error) {
      this.errorMessage = getErrorMessage(error);
    } finally {
      this.loading = false;
    }
  }

  async mounted() {
    if (!this.organization) {
      return;
    }
    
    this.loading = true;
    try {
      this.tokens = await this.getTokens(this.organization.id);
      this.invitations = await this.getInvitations(this.organization.id);
      this.groups = await this.getGroups(this.organization.id);
    } 
    catch (err) {
      this.errorMessage = getErrorMessage(err);
    }
    finally {
      this.loading = false;
    }
  }

}
